/**
 * Canonical registry of every filterable table in the app.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `genericTableStore.appliedFilters` is keyed by a free-form `tableName` string
 * that used to be hardcoded at each `<GenericTable tableName="..." />` call
 * site. The same string is ALSO the primary key of a saved filter view on BE
 * (`saved_filter_view.tabName`), so a typo silently orphans a user's saved
 * views and a rename destroys them.
 *
 * ⚠️ THE STRING VALUES BELOW ARE A BE DATA CONTRACT — DO NOT "TIDY" THEM.
 * Several read badly (`'candidates-Submission Ready Candidate'`, the lowercase
 * `'jobs-upcoming assignment Jobs'`); they are kept verbatim because saved
 * views already exist against them in production. Only ADD entries here.
 */
export const TABLE_NAMES = {
	// --- Candidates page -----------------------------------------------
	CANDIDATES_ON_ASSIGNMENT: 'candidates-on assignment',
	CANDIDATES_PER_DIEM_ELIGIBLE: 'candidates-Per Diem Eligible Candidate',
	CANDIDATES_ACTIVE_SUBMISSION: 'candidates-active submission',
	CANDIDATES_SUBMISSION_READY: 'candidates-Submission Ready Candidate',
	CANDIDATES_MATCH: 'candidates-match',
	CANDIDATES_PRE_SUBMISSION: 'candidates-pre-submission',
	CANDIDATES_SHORTLIST: 'candidates-Shortlist Jobs',
	CANDIDATES_DISMISSED: 'candidates-Dismissed Jobs',
	// Maestro V2 lists. Their own keys, so a filter or saved view on one never leaks onto another.
	CANDIDATES_ALL: 'candidates-all',
	CANDIDATES_ACTIVE: 'candidates-active',
	CANDIDATES_ASSIGNED_TO_ME: 'candidates-assigned-to-me',
	CANDIDATES_V2_NEW: 'candidates-v2-new',
	CANDIDATES_V2_PROFILE_INCOMPLETE: 'candidates-v2-profile-incomplete',

	// --- Jobs page ------------------------------------------------------
	JOBS_AVAILABLE: 'jobs-available Jobs',
	JOBS_SHIFT: 'jobs-shift Jobs',
	JOBS_SUBMISSIONS: 'jobs-submissions',
	JOBS_MATCH: 'jobs-match',
	JOBS_ACTIVE_ASSIGNMENT: 'jobs-active assignment Jobs',
	JOBS_UPCOMING_ASSIGNMENT: 'jobs-upcoming assignment Jobs',
	JOBS_CLOSED: 'jobs-closed Jobs',
	JOBS_SHORTLIST: 'jobs-Shortlisted Candidates',
	JOBS_DISMISSED: 'jobs-Dismissed Candidates',
	// Maestro V2 Jobs board: one table per tile-tab (see recruiter-v2 jobs-tabs), so
	// the filters and saved views of one never leak into another.
	JOBS_V2_TOAS_ALL: 'jobs-v2-toas-all',
	JOBS_V2_TOAS: 'jobs-v2-toas',
	JOBS_V2_INTERNAL: 'jobs-v2-internal-submissions',
	JOBS_V2_EXTERNAL: 'jobs-v2-external-submissions',
	JOBS_V2_ACTIVE_APPLICATIONS: 'jobs-v2-active-applications',
	JOBS_V2_NEW_JOBS: 'jobs-v2-new-jobs',

	// --- Credentials page (Maestro V2) -----------------------------------
	// One table per credential state, because the API filters by one status at a
	// time; each keeps its own columns, filters and saved views.
	CREDENTIALS_V2_ALL: 'credentials-v2-all',
	CREDENTIALS_V2_MISSING: 'credentials-v2-missing-for-submission',
	CREDENTIALS_V2_IN_VERIFICATION: 'credentials-v2-in-review',
	CREDENTIALS_V2_EXPIRING: 'credentials-v2-expiring',
	CREDENTIALS_V2_EXPIRED_ON_TOA: 'credentials-v2-expired-on-assignment',

	// --- Job detail page -------------------------------------------------
	JOB_DETAIL_SUBMISSION: 'job-detail-submission',
	// Maestro V2 splits the one Submissions table by stage. Each gets its own key so a
	// filter or saved view on one never leaks onto another.
	JOB_DETAIL_EXTERNAL_SUBMISSION: 'job-detail-external-submission',
	JOB_DETAIL_INTERNAL_SUBMISSION: 'job-detail-internal-submission',
	JOB_DETAIL_ACTIVE_APPLICATION: 'job-detail-active-application',
	JOB_DETAIL_MATCH: 'job-detail-matches',
	JOB_DETAIL_SHORTLIST: 'job-detail-Shortlisted Candidates',
	JOB_DETAIL_DISMISSED: 'job-detail-Dismissed Candidates',

	// --- Home page --------------------------------------------------------
	HOME_SUBMISSION: 'home-submission',
	HOME_MATCH: 'home-match',
	HOME_PRE_SUBMISSION: 'home-pre-submission',
	HOME_RECENT_APPLICATIONS: 'home-recent-applications',
	// Reports & Analytics › Applicant sources (SPT-2326).
	APPLICANT_SOURCES: 'reports-applicant-sources',
	HOME_SHORTLIST: 'home-Shortlist Jobs',
	HOME_DISMISSED: 'home-Dismissed Jobs',

	// --- Misc --------------------------------------------------------------
	ROLE: 'role',
	CANDIDATE_APP_ACCESS: 'candidate-app-access',
}

/**
 * Human-readable "Page › Tab" label, shown wherever a table has to be named
 * to the user — e.g. "Apply the view saved on Candidates › Matches here?".
 * The raw TABLE_NAMES values are storage keys and are not presentable.
 */
export const TABLE_LABELS = {
	[TABLE_NAMES.CANDIDATES_ON_ASSIGNMENT]: 'Candidates › On Assignment',
	[TABLE_NAMES.CANDIDATES_PER_DIEM_ELIGIBLE]: 'Candidates › Per Diem Eligible',
	[TABLE_NAMES.CANDIDATES_ACTIVE_SUBMISSION]: 'Candidates › Active Submission',
	[TABLE_NAMES.CANDIDATES_SUBMISSION_READY]: 'Candidates › Submission Ready',
	[TABLE_NAMES.CANDIDATES_MATCH]: 'Candidates › Matches',
	[TABLE_NAMES.CANDIDATES_PRE_SUBMISSION]: 'Candidates › Pipeline',
	[TABLE_NAMES.CANDIDATES_SHORTLIST]: 'Candidates › Shortlist',
	[TABLE_NAMES.CANDIDATES_DISMISSED]: 'Candidates › Dismissed',
	[TABLE_NAMES.CANDIDATES_ALL]: 'Candidates › All Candidates',
	[TABLE_NAMES.CANDIDATES_ACTIVE]: 'Candidates › Active Candidates',
	[TABLE_NAMES.CANDIDATES_ASSIGNED_TO_ME]: 'Candidates › Assigned to Me',
	[TABLE_NAMES.CANDIDATES_V2_NEW]: 'Candidates › New Sign-ups',
	[TABLE_NAMES.CANDIDATES_V2_PROFILE_INCOMPLETE]: 'Candidates › Profile Incomplete',
	[TABLE_NAMES.JOBS_AVAILABLE]: 'Jobs › Available Jobs',
	[TABLE_NAMES.JOBS_SHIFT]: 'Jobs › Shift Jobs',
	[TABLE_NAMES.JOBS_SUBMISSIONS]: 'Jobs › Submissions',
	[TABLE_NAMES.JOBS_MATCH]: 'Jobs › Matches',
	[TABLE_NAMES.JOBS_ACTIVE_ASSIGNMENT]: 'Jobs › Active Assignments',
	[TABLE_NAMES.JOBS_UPCOMING_ASSIGNMENT]: 'Jobs › Upcoming Assignments',
	[TABLE_NAMES.JOBS_CLOSED]: 'Jobs › Closed Jobs',
	[TABLE_NAMES.JOBS_SHORTLIST]: 'Jobs › Shortlisted Candidates',
	[TABLE_NAMES.JOBS_DISMISSED]: 'Jobs › Dismissed Candidates',
	[TABLE_NAMES.JOBS_V2_TOAS_ALL]: 'Jobs › All Assignments',
	[TABLE_NAMES.JOBS_V2_TOAS]: 'Jobs › TOAs',
	[TABLE_NAMES.JOBS_V2_INTERNAL]: 'Jobs › Internal submissions',
	[TABLE_NAMES.JOBS_V2_EXTERNAL]: 'Jobs › External submissions',
	[TABLE_NAMES.JOBS_V2_ACTIVE_APPLICATIONS]: 'Jobs › Active applications',
	[TABLE_NAMES.JOBS_V2_NEW_JOBS]: 'Jobs › New jobs',
	[TABLE_NAMES.CREDENTIALS_V2_ALL]: 'Credentials › All',
	[TABLE_NAMES.CREDENTIALS_V2_MISSING]: 'Credentials › Missing for a Client Submission',
	[TABLE_NAMES.CREDENTIALS_V2_IN_VERIFICATION]: 'Credentials › Uploaded for Review',
	[TABLE_NAMES.CREDENTIALS_V2_EXPIRING]: 'Credentials › Expiring Soon',
	[TABLE_NAMES.CREDENTIALS_V2_EXPIRED_ON_TOA]: 'Credentials › Expired on an Assignment',
	[TABLE_NAMES.JOB_DETAIL_SUBMISSION]: 'Job Detail › Submissions',
	[TABLE_NAMES.JOB_DETAIL_EXTERNAL_SUBMISSION]: 'Job Detail › External Submissions',
	[TABLE_NAMES.JOB_DETAIL_INTERNAL_SUBMISSION]: 'Job Detail › Internal Submissions',
	[TABLE_NAMES.JOB_DETAIL_ACTIVE_APPLICATION]: 'Job Detail › Active Applications',
	[TABLE_NAMES.JOB_DETAIL_MATCH]: 'Job Detail › Matches',
	[TABLE_NAMES.JOB_DETAIL_SHORTLIST]: 'Job Detail › Shortlisted Candidates',
	[TABLE_NAMES.JOB_DETAIL_DISMISSED]: 'Job Detail › Dismissed Candidates',
	[TABLE_NAMES.HOME_SUBMISSION]: 'Home › Submissions',
	[TABLE_NAMES.HOME_MATCH]: 'Home › Matches',
	[TABLE_NAMES.HOME_PRE_SUBMISSION]: 'Home › Pipeline',
	[TABLE_NAMES.HOME_RECENT_APPLICATIONS]: 'Home › Recent Applications',
	[TABLE_NAMES.APPLICANT_SOURCES]: 'Reports › Campaign tracking',
	[TABLE_NAMES.HOME_SHORTLIST]: 'Home › Shortlist',
	[TABLE_NAMES.HOME_DISMISSED]: 'Home › Dismissed',
	[TABLE_NAMES.ROLE]: 'User Roles',
	[TABLE_NAMES.CANDIDATE_APP_ACCESS]: 'Candidate App Access',
}

export const getTableLabel = (tableName) => TABLE_LABELS[tableName] || tableName || ''

/**
 * Tables belonging to each page, used to scope a page-level reset (the X in
 * the universal search box) to that page instead of wiping every table's
 * filters app-wide.
 */
export const PAGE_TABLE_NAMES = {
	CANDIDATES: [
		TABLE_NAMES.CANDIDATES_ON_ASSIGNMENT,
		TABLE_NAMES.CANDIDATES_PER_DIEM_ELIGIBLE,
		TABLE_NAMES.CANDIDATES_ACTIVE_SUBMISSION,
		TABLE_NAMES.CANDIDATES_SUBMISSION_READY,
		TABLE_NAMES.CANDIDATES_MATCH,
		TABLE_NAMES.CANDIDATES_PRE_SUBMISSION,
		TABLE_NAMES.CANDIDATES_SHORTLIST,
		TABLE_NAMES.CANDIDATES_DISMISSED,
		TABLE_NAMES.CANDIDATES_ALL,
		TABLE_NAMES.CANDIDATES_ACTIVE,
		TABLE_NAMES.CANDIDATES_ASSIGNED_TO_ME,
		TABLE_NAMES.CANDIDATES_V2_NEW,
		TABLE_NAMES.CANDIDATES_V2_PROFILE_INCOMPLETE,
	],
	CREDENTIALS: [
		TABLE_NAMES.CREDENTIALS_V2_ALL,
		TABLE_NAMES.CREDENTIALS_V2_MISSING,
		TABLE_NAMES.CREDENTIALS_V2_IN_VERIFICATION,
		TABLE_NAMES.CREDENTIALS_V2_EXPIRING,
		TABLE_NAMES.CREDENTIALS_V2_EXPIRED_ON_TOA,
	],
	JOBS: [
		TABLE_NAMES.JOBS_AVAILABLE,
		TABLE_NAMES.JOBS_SHIFT,
		TABLE_NAMES.JOBS_SUBMISSIONS,
		TABLE_NAMES.JOBS_MATCH,
		TABLE_NAMES.JOBS_ACTIVE_ASSIGNMENT,
		TABLE_NAMES.JOBS_UPCOMING_ASSIGNMENT,
		TABLE_NAMES.JOBS_CLOSED,
		TABLE_NAMES.JOBS_SHORTLIST,
		TABLE_NAMES.JOBS_DISMISSED,
		TABLE_NAMES.JOBS_V2_TOAS,
		TABLE_NAMES.JOBS_V2_TOAS_ALL,
		TABLE_NAMES.JOBS_V2_INTERNAL,
		TABLE_NAMES.JOBS_V2_EXTERNAL,
		TABLE_NAMES.JOBS_V2_ACTIVE_APPLICATIONS,
		TABLE_NAMES.JOBS_V2_NEW_JOBS,
	],
}

/**
 * Inner (expanded-row) tables. They share the filter store but are never a
 * saved-view target — a view only makes sense against the outer list.
 */
export const INNER_TABLE_NAMES = {
	CANDIDATES_INNER_MATCH: 'candidates-inner-match',
	HOME_INNER_MATCH: 'home-inner-match',
}

const INNER_TABLE_NAME_SET = new Set(Object.values(INNER_TABLE_NAMES))

/**
 * Tables that intentionally get NO saved-views UI.
 *
 * Only the inner expanded-row tables, and that is a structural fact rather than
 * a judgement: an inner table lives inside one expanded row, has no toolbar to
 * put the control in, and "the view I use for this one row" means nothing.
 *
 * Shortlist, Dismissed and the Roles admin table used to be listed here on the
 * grounds that they carry no filterable business dimension, so a saved view
 * would always be empty. That reasoning only holds if a view is filters — it is
 * not. It carries the column layout and the sort as well, and those tables have
 * seven or eight columns each and more minimum width than a screen. "Job, Talent
 * and Created At only, newest first" is a real thing to save on Dismissed Jobs,
 * and the whole point of the control is that you do not rebuild it every visit.
 */
const SAVED_VIEWS_EXCLUDED = new Set([])

export const supportsSavedViews = (tableName) => Boolean(tableName) && !INNER_TABLE_NAME_SET.has(tableName) && !SAVED_VIEWS_EXCLUDED.has(tableName)
